import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../services/supabase';
import { useGroups } from '../hooks/useGroups';
import { useAuth } from '../context/AuthContext';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const Container = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const FormCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
`;

const Field = styled.div`
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const Select = styled.select`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const TextArea = styled.textarea`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  min-height: 120px;
`;

const Button = styled.button`
  background-color: var(--primary);
  color: #fff;
  padding: 0.6rem 1.1rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  box-shadow: 0 10px 18px rgba(31, 117, 254, 0.25);
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SecondaryButton = styled(Button)`
  background-color: var(--surface);
  color: var(--onSurface);
  border: 1px solid var(--outline);
  box-shadow: none;
`;

const ErrorText = styled.p`
  color: #e53e3e;
`;

const CreateAnnouncementPage: React.FC = () => {
  const navigate = useNavigate();
  const { announcementId } = useParams<{ announcementId: string }>();
  const isEditMode = Boolean(announcementId);
  const { user } = useAuth();
  const { data: groups, isLoading: groupsLoading } = useGroups();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [externalLink, setExternalLink] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('none');
  const [submitting, setSubmitting] = useState(false);
  const [submitAction, setSubmitAction] = useState<'draft' | 'published'>('published');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loadingAnnouncement, setLoadingAnnouncement] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ministryOptions = React.useMemo(() => {
    const allGroups = groups ?? [];
    const ministryGroups = allGroups.filter((group) => group.category === 'ministry');
    const source = ministryGroups.length > 0 ? ministryGroups : allGroups;
    return [...source].sort((a, b) => a.name.localeCompare(b.name));
  }, [groups]);

  const uploadToStorage = async (file: File) => {
    const extension = file.name.split('.').pop() || 'jpg';
    const fileName = `announcement_${Date.now()}_${Math.random().toString(36).slice(2)}.${extension}`;
    const { error: uploadError } = await supabase.storage
      .from('post-images')
      .upload(fileName, file, { upsert: false });
    if (uploadError) {
      throw new Error(uploadError.message);
    }
    const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
    return data.publicUrl;
  };

  const handleImageUpload = async (file?: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Please upload an image smaller than 5MB.');
      return;
    }
    setError(null);
    setUploadingImage(true);
    try {
      const url = await uploadToStorage(file);
      setImageUrl(url);
    } catch (err: any) {
      setError(err.message || 'Image upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (!user?.id) {
        throw new Error('You must be signed in to create announcements.');
      }

      if (isEditMode && announcementId) {
        const updatePayload = {
          title: title.trim(),
          content: content.trim(),
          images: imageUrl ? [imageUrl] : [],
          external_link: externalLink.trim() || null,
          status: submitAction,
        };
        const { error: updateError } = await supabase
          .from('wall_posts')
          .update(updatePayload)
          .eq('id', announcementId)
          .eq('type', 'home_announcement');

        if (updateError) throw updateError;
        navigate('/announcements');
        return;
      }

      const shouldCreateGroupPost = selectedGroupId !== 'none';
      const selectedGroup = shouldCreateGroupPost
        ? groups?.find((group) => group.id === selectedGroupId)
        : null;

      if (shouldCreateGroupPost && !selectedGroup) {
        throw new Error('Selected ministry could not be found. Please refresh and try again.');
      }

      const wallPostPayload = {
        title: title.trim(),
        content: content.trim(),
        images: imageUrl ? [imageUrl] : [],
        external_link: externalLink.trim() || null,
        type: 'home_announcement',
        status: submitAction,
      };

      const { data: wallPostRow, error: wallPostError } = await supabase
        .from('wall_posts')
        .insert(wallPostPayload)
        .select('id')
        .single();

      if (wallPostError) throw wallPostError;

      const authorName =
        (typeof user.user_metadata?.full_name === 'string' && user.user_metadata.full_name.trim()) ||
        (typeof user.email === 'string' && user.email.trim()) ||
        'Admin';

      const announcementText = [title.trim(), content.trim()]
        .filter(Boolean)
        .join('\n\n');
      const announcementWithLink = externalLink.trim()
        ? `${announcementText}\n\n${externalLink.trim()}`
        : announcementText;

      if (submitAction === 'published' && shouldCreateGroupPost && selectedGroup) {
        const { error: groupPostError } = await supabase.from('group_posts').insert({
          group_id: selectedGroup.id,
          content: announcementWithLink || title.trim(),
          type: 'announcement',
          images: imageUrl ? [imageUrl] : [],
          status: 'published',
          author_id: user.id,
          author_name: authorName,
          likes: 0,
          comments: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

        if (groupPostError) {
          if (wallPostRow?.id) {
            await supabase.from('wall_posts').delete().eq('id', wallPostRow.id);
          }
          throw groupPostError;
        }
      }

      navigate('/announcements');
    } catch (err: any) {
      setError(err.message || 'Failed to create announcement');
    } finally {
      setSubmitting(false);
    }
  };

  React.useEffect(() => {
    if (!isEditMode || !announcementId) return;

    const loadAnnouncement = async () => {
      setLoadingAnnouncement(true);
      setError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from('wall_posts')
          .select('id, title, content, images, external_link, type')
          .eq('id', announcementId)
          .eq('type', 'home_announcement')
          .single();

        if (fetchError) throw fetchError;

        setTitle(data?.title ?? '');
        setContent(data?.content ?? '');
        setImageUrl(Array.isArray(data?.images) && data.images.length > 0 ? data.images[0] : '');
        setExternalLink(data?.external_link ?? '');
      } catch (err: any) {
        setError(err.message || 'Failed to load announcement');
      } finally {
        setLoadingAnnouncement(false);
      }
    };

    loadAnnouncement();
  }, [announcementId, isEditMode]);

  return (
    <Page>
      <Container>
        <h1 style={{ marginBottom: '1rem' }}>{isEditMode ? 'Edit Announcement' : 'Create Announcement'}</h1>
        <FormCard>
          <form onSubmit={handleSubmit}>
        {loadingAnnouncement ? <p>Loading announcement…</p> : null}
        <Field>
          <Label htmlFor="title">Title</Label>
          <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </Field>
        {!isEditMode ? (
          <Field>
            <Label htmlFor="ministry">Ministry</Label>
            <Select
              id="ministry"
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              disabled={groupsLoading || submitting}
            >
              <option value="none">None (Home + Announcements only)</option>
              {ministryOptions.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field>
          <Label htmlFor="content">Content</Label>
          <TextArea id="content" value={content} onChange={(e) => setContent(e.target.value)} />
        </Field>
        <Field>
          <Label htmlFor="image">Announcement Image (optional)</Label>
          <Input
            id="image"
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e.target.files?.[0])}
            disabled={uploadingImage}
          />
        </Field>
        <Field>
          <Label htmlFor="imageUrl">Image URL</Label>
          <Input
            id="imageUrl"
            value={imageUrl}
            readOnly
            placeholder={uploadingImage ? 'Uploading image...' : 'Upload an image to set URL'}
          />
        </Field>
        <Field>
          <Label htmlFor="link">External Link (optional)</Label>
          <Input id="link" value={externalLink} onChange={(e) => setExternalLink(e.target.value)} />
        </Field>
        {error && <ErrorText>{error}</ErrorText>}
        <ButtonRow>
          <SecondaryButton
            type="submit"
            disabled={submitting || groupsLoading || loadingAnnouncement}
            onClick={() => setSubmitAction('draft')}
          >
            {submitting && submitAction === 'draft' ? 'Saving...' : 'Save Draft'}
          </SecondaryButton>
          <Button
            type="submit"
            disabled={submitting || groupsLoading || loadingAnnouncement}
            onClick={() => setSubmitAction('published')}
          >
            {submitting && submitAction === 'published' ? 'Publishing...' : 'Publish'}
          </Button>
        </ButtonRow>
          </form>
        </FormCard>
      </Container>
    </Page>
  );
};

export default CreateAnnouncementPage;
